import { Form, Input, Modal, Select, message } from "antd";
import { COUNTRIES } from "common/constants";
import { useState } from "react";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import {
  useCreateEmployerMutation,
  useUpdateEmployerMutation,
} from "features/employersTable/employersAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const getPostalCodeMaxLength = (country) => {
  if (country === "US") return 5;
  if (country === "CA") return 7;
  return undefined;
};

export default function EmployerModal({ title, onOk, onCancel, selectedEmployer }) {
  const [form] = Form.useForm();
  const { data: constants } = useFetchBackendConstantsQuery();
  const [addEmployer] = useCreateEmployerMutation();
  const [updateEmployer] = useUpdateEmployerMutation();

  const [country, setCountry] = useState(selectedEmployer?.address?.country ?? "US");

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const onCountryChange = (value) => {
    setCountry(value);
    form.setFieldsValue({ address: { state: undefined, zipCode: undefined } });
  };

  const onSubmit = async () => {
    const fields = await form.validateFields();
    // Remove empty address and contact objects otherwise the payload will be rejected
    if (
      Object.keys(fields.address)
        .filter((key) => key !== "country")
        .every((key) => undefined === fields.address[key])
    ) {
      fields.address = null;
    }
    if (Object.keys(fields.contact).every((key) => undefined === fields.contact[key])) {
      fields.contact = null;
    }
    const result = selectedEmployer
      ? await updateEmployer({ ...fields })
      : await addEmployer({ ...fields });

    if ("data" in result) {
      message.success("Employer saved successfully.");
      onOk();
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
      message.error(`Failed to save employer. Please try again later.`);
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={title}
      okText="Submit"
      open
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        form={form}
        layout="vertical"
        validateMessages={{ required: "This is a required field" }}
        initialValues={{ address: { country: "US" }, ...selectedEmployer }}
      >
        <Form.Item name="id" hidden>
          <Input hidden />
        </Form.Item>
        <Form.Item name="code" label="Employer Code" rules={[{ required: true }]}>
          <Input placeholder="Enter Employer Code..." />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Enter Name..." />
        </Form.Item>
        <Form.Item name="ein" label="EIN">
          <Input placeholder="Enter EIN..." />
        </Form.Item>
        <Form.Item name="website" label="Website">
          <Input placeholder="Enter Website..." />
        </Form.Item>
        <Form.Item name={["address", "address1"]} label="Address Line 1">
          <Input placeholder="Enter Address..." />
        </Form.Item>
        <Form.Item name={["address", "address2"]} label="Address Line 2">
          <Input placeholder="Enter Address..." />
        </Form.Item>
        <Form.Item
          name={["address", "city"]}
          label="City"
          rules={[{ pattern: /^[^\d]*$/, message: "Please enter a valid city" }]}
        >
          <Input placeholder="Enter City..." />
        </Form.Item>
        <Form.Item name={["address", "country"]} label="Country">
          <Select
            placeholder="Select a country..."
            showSearch
            filterOption={filterOption}
            options={COUNTRIES}
            onChange={onCountryChange}
          />
        </Form.Item>
        <Form.Item name={["address", "state"]} label="State/Province">
          {country === "US" || country === "CA" ? (
            <Select
              placeholder="Select one..."
              options={(country === "CA" ? constants?.canadianProvinces : constants?.states)?.map(
                (state) => ({
                  label: `${state.display} (${state.value})`,
                  value: state.value,
                }),
              )}
              filterOption={filterOption}
              showSearch
            />
          ) : (
            <Input placeholder="Enter State/Province..." />
          )}
        </Form.Item>
        <Form.Item
          name={["address", "zipCode"]}
          label="Zip/Postal Code"
          dependencies={[["address", "country"]]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const selectedCountry = getFieldValue(["address", "country"]);
                if (selectedCountry !== "US") {
                  return Promise.resolve();
                }
                if (!value || /^\d{5}(-\d{4})?$/.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Must be a valid zip code"));
              },
            }),
          ]}
        >
          <Input
            placeholder={country === "CA" ? "A1A 1A1" : "Enter Zip/Postal Code..."}
            maxLength={getPostalCodeMaxLength(country)}
          />
        </Form.Item>
        <Form.Item name={["contact", "name"]} label="Contact Name">
          <Input placeholder="Enter Contact Name..." />
        </Form.Item>
        <Form.Item name={["contact", "workPhone"]} label="Work Phone">
          <Input placeholder="XXX-XXX-XXXX" />
        </Form.Item>
        <Form.Item name={["contact", "cellPhone"]} label="Cell Phone">
          <Input placeholder="XXX-XXX-XXXX" />
        </Form.Item>
        <Form.Item name={["contact", "email"]} label="Email">
          <Input placeholder="Enter Email..." />
        </Form.Item>
        <Form.Item name="notes" label="Notes / Description">
          <Input placeholder="Enter Notes..." />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}
